<template>
  <div class="ccma-password-change">
    <div class="password-section">
      <div class="section-header">
        <h4>{{ t('consultantCorner.security.passwordHeader') }}</h4>
        <p class="subtitle">{{ t('consultantCorner.security.passwordSubHeader') }}</p>
      </div>

      <div class="password-form">
        <div class="form-group">
          <label>{{ t('consultantCorner.security.oldPasswordLabel') }}</label>
          <input 
            v-model="passwordData.oldPassword" 
            type="password" 
            class="form-control"
            :class="{ 'is-invalid': passwordErrors.oldPassword }"
            :placeholder="t('consultantCorner.security.oldPasswordPlaceholder')"
          />
          <div v-if="passwordErrors.oldPassword" class="invalid-feedback">{{ passwordErrors.oldPassword }}</div>
        </div>

        <div class="form-group">
          <label>{{ t('consultantCorner.security.newPasswordLabel') }}</label>
          <input 
            v-model="passwordData.newPassword" 
            type="password" 
            class="form-control"
            :class="{ 'is-invalid': passwordErrors.newPassword }"
            :placeholder="t('consultantCorner.security.newPasswordPlaceholder')"
          />
          <div v-if="passwordErrors.newPassword" class="invalid-feedback">{{ passwordErrors.newPassword }}</div>
        </div>

        <div class="form-group">
          <label>{{ t('consultantCorner.security.confirmPasswordLabel') }}</label>
          <input 
            v-model="passwordData.confirmPassword" 
            type="password" 
            class="form-control"
            :class="{ 'is-invalid': passwordErrors.confirmPassword }"
            :placeholder="t('consultantCorner.security.confirmPasswordPlaceholder')"
          />
          <div v-if="passwordErrors.confirmPassword" class="invalid-feedback">{{ passwordErrors.confirmPassword }}</div>
        </div>

        <!-- Password Requirements -->
        <div class="password-requirements">
          <div class="requirements-header">
            <i class="fas fa-info-circle"></i>
            <span>{{ t('consultantCorner.security.passwordRequirements') }}</span>
          </div>
          <ul class="requirements-list">
            <li :class="{ 'requirement-met': passwordRequirements.minLength }">
              <i class="fas fa-check requirement-check"></i>
              {{ t('consultantCorner.security.requirement1') }}
            </li>
            <li :class="{ 'requirement-met': passwordRequirements.hasNumber }">
              <i class="fas fa-check requirement-check"></i>
              {{ t('consultantCorner.security.requirement2') }}
            </li>
            <li :class="{ 'requirement-met': passwordRequirements.hasLowercase }">
              <i class="fas fa-check requirement-check"></i>
              {{ t('consultantCorner.security.requirement3') }}
            </li>
            <li :class="{ 'requirement-met': passwordRequirements.hasUppercase }">
              <i class="fas fa-check requirement-check"></i>
              {{ t('consultantCorner.security.requirement4') }}
            </li>
            <li :class="{ 'requirement-met': passwordRequirements.hasSpecialChar }">
              <i class="fas fa-check requirement-check"></i>
              {{ t('consultantCorner.security.requirement5') }}
            </li>
          </ul>
        </div>

        <div class="form-actions">
          <button 
            @click="savePassword" 
            class="btn-update"
            :disabled="isSavingPassword"
          >
            <span v-if="isSavingPassword">{{ t('consultantCorner.security.saving') }}</span>
            <span v-else>{{ t('consultantCorner.security.savePassword') }}</span>
          </button>
        </div>

        <!-- Password Success notification -->
        <div v-if="showPasswordSuccessMessage" class="success-notification">
          <i class="fas fa-check-circle"></i>
          <span>{{ t('consultantCorner.security.passwordSaveSuccess') }}</span>
        </div>

        <!-- Password Error notification -->
        <div v-if="passwordSaveError" class="error-notification">
          <i class="fas fa-exclamation-triangle"></i>
          <span>{{ passwordSaveError }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { PwsProfileAccountInfoSave } from '../../api/AccountInfo'
import type { IPwsProfileAccountInfoSaveRequest } from '../../types/AccountInfoTypes'
import { GetPasswordRegex, GetBeeNumberAuthenicatedUser } from '../../stores/SettingsStore'

const { t } = useI18n()
const logger = useLogger()

const passwordData = ref({
  oldPassword: '',
  newPassword: '',
  confirmPassword: ''
})

const passwordErrors = ref<Record<string, string>>({})
const isSavingPassword = ref(false)
const passwordSaveError = ref('')
const showPasswordSuccessMessage = ref(false)
const passwordRegex = ref('')

const passwordRequirements = ref({
  minLength: false,
  hasNumber: false,
  hasLowercase: false,
  hasUppercase: false,
  hasSpecialChar: false
})

const loadPasswordRegex = async () => {
  try {
    passwordRegex.value = await GetPasswordRegex()
  } catch (error) {
    logger.error('Error loading password regex:', error)
    // Fallback regex
    passwordRegex.value = '^.*(?=.{8,})(?=.*\\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!#$%\\-_=+@]).*$'
  }
}

const checkPasswordRequirements = (password: string) => {
  passwordRequirements.value = {
    minLength: password.length >= 8,
    hasNumber: /\d/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasUppercase: /[A-Z]/.test(password),
    hasSpecialChar: /[!#$%\-_=+@]/.test(password)
  }
}

watch(() => passwordData.value.newPassword, (newValue) => {
  checkPasswordRequirements(newValue)
})

onMounted(async () => {
  await loadPasswordRegex()
})

const validatePasswordForm = (): boolean => {
  passwordErrors.value = {}
  
  // Validate old password
  if (!passwordData.value.oldPassword.trim()) {
    passwordErrors.value.oldPassword = t('consultantCorner.security.oldPasswordRequired')
  }
  
  // Validate new password
  if (!passwordData.value.newPassword.trim()) {
    passwordErrors.value.newPassword = t('consultantCorner.security.newPasswordRequired')
  } else {
    // Validate password strength
    const regex = new RegExp(passwordRegex.value)
    if (!regex.test(passwordData.value.newPassword)) {
      passwordErrors.value.newPassword = t('consultantCorner.security.newPasswordWeak')
    }
  }
  
  // Validate confirm password
  if (!passwordData.value.confirmPassword.trim()) {
    passwordErrors.value.confirmPassword = t('consultantCorner.security.confirmPasswordRequired')
  } else if (passwordData.value.newPassword !== passwordData.value.confirmPassword) {
    passwordErrors.value.confirmPassword = t('consultantCorner.security.passwordsDoNotMatch')
  }
  
  return Object.keys(passwordErrors.value).length === 0
}

const savePassword = async () => {
  if (!validatePasswordForm()) {
    return
  }
  
  isSavingPassword.value = true
  passwordSaveError.value = ''
  showPasswordSuccessMessage.value = false
  
  try {
    const beeNumber = await GetBeeNumberAuthenicatedUser()
    const request: IPwsProfileAccountInfoSaveRequest = {
      beeNumber: beeNumber || 0,
      oldPassword: passwordData.value.oldPassword,
      newPassword: passwordData.value.newPassword
    }
    
    await PwsProfileAccountInfoSave(request)
    
    // Clear password fields
    passwordData.value = {
      oldPassword: '',
      newPassword: '',
      confirmPassword: ''
    }
    
    // Show success message
    showPasswordSuccessMessage.value = true
    logger.info(t('consultantCorner.security.passwordSaveSuccess'))
    
    // Auto-hide success message after 3 seconds
    setTimeout(() => {
      showPasswordSuccessMessage.value = false
    }, 3000)
    
  } catch (error) {
    logger.error('Error saving password:', error)
    passwordSaveError.value = t('consultantCorner.security.passwordSaveError')
    
    // Auto-hide error message after 5 seconds
    setTimeout(() => {
      passwordSaveError.value = ''
    }, 5000)
  } finally {
    isSavingPassword.value = false
  }
}
</script>

<style lang="scss" scoped>
.ccma-password-change {
  padding: $card-padding;
  max-width: 800px;
  margin: 0 auto;
}

.password-section {
  padding: 2rem;
  background-color: var(--vt-c-white-soft);
  border-radius: $radius-md;
  border: 1px solid #e9ecef;
}

.section-header {
  margin-bottom: 2rem;
  text-align: center;
}

.section-header h4 {
  @include typography-h3;
  @include font-weight-semibold;
  color: #e67e22;
  margin-bottom: 0.5rem;
}

.section-header .subtitle {
  @include typography-body;
  color: #666;
  margin-bottom: 0;
}

.password-form {
  max-width: 500px;
  margin: 0 auto;
}

.password-requirements {
  margin: 1.5rem 0;
  padding: 1.25rem;
  background-color: var(--vt-c-white-soft);
  border: 1px solid #e9ecef;
  border-radius: $radius-md;
}

.requirements-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  @include font-weight-semibold;
  margin-bottom: 0.75rem;
  color: #495057;
}

.requirements-header i {
  color: #6c757d;
}

.requirements-list {
  margin: 0;
  padding-left: 0;
  list-style: none;
  color: #6c757d;
}

.requirements-list li {
  @include typography-body;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
}

.requirement-check {
  @include typography-small;
  opacity: 0;
  color: #28a745;
  transition: opacity 0.3s ease;
}

.requirements-list li.requirement-met {
  @include font-weight-medium;
  color: #28a745;
}

.requirements-list li.requirement-met .requirement-check {
  opacity: 1;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-group label {
  @include font-weight-semibold;
  margin-bottom: 0.5rem;
  display: block;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  border: none;
  border-bottom: 2px solid #e9ecef;
  @include typography-small;
  border-radius: 0;
  background-color: transparent;
  transition: all 0.3s ease;
}

.form-control:focus {
  border-bottom-color: #e67e22;
  outline: none !important;
  box-shadow: none;
  background-color: var(--vt-c-white-soft);
}

.form-control.is-invalid {
  border-bottom-color: #dc3545;
  background-color: #fff5f5;
}

.invalid-feedback {
  @include typography-small;
  color: #dc3545;
  margin-top: 0.25rem;
  display: block;
}

.form-actions {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e9ecef;
  text-align: center;
}

.btn-update {
  padding: $spacing-2-5 $spacing-5;
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
  border: none;
  @include typography-body;
  @include font-weight-medium;
  border-radius: $radius-sm;
  cursor: pointer;
}

.btn-update:hover {
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  filter: brightness(0.85);
}

.btn-update:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background-color: #6c757d;
}

.success-notification, .error-notification {
  margin-top: 1rem;
  padding: 1rem;
  border-radius: $radius-md;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  animation: slideIn 0.3s ease-out;
}

.success-notification {
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
}

.success-notification i {
  color: #28a745;
}

.error-notification {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}

.error-notification i {
  color: #dc3545;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 767px) {
  .ccma-password-change {
    padding: $spacing-4;
  }
  
  .password-section {
    padding: 1rem;
  }
  
  .section-header h4 {
    @include typography-h4;
  }
  
  .btn-update {
    width: 100%;
  }
}
</style>
